import { containsCoordinate } from "ol/extent";
import Overlay from "ol/Overlay";
import { Style, Fill, Stroke } from "ol/style";
import CircleStyle from "ol/style/Circle";

/**
 * Map mixin to handle point layer point highlighting (on hover and click)
 * and selecting (render overlay on click).
 */
export default {
  data() {
    return {
      // point under cursor (hover) -> watcher handles highlighting
      currentPointLayerHoverPoint: null,
      // selected point (click) -> watcher handles highlighting
      selectedPoint: null,
      // map overlay for selected point (info popup)
      selectedPointPopupOverlay: null
    };
  },
  mounted() {
    // init overlay
    this.selectedPointPopupOverlay = new Overlay({
      element: document.getElementById("selected-point-popup"),
      positioning: "bottom-center",
      offset: [0, -15]
    });

    // listeners for highlighting/selecting events emitted by other components,
    // e.g. SelectedPointLayersInfoTable
    window.EventBus.$on("highlightPointOnMap", point => {
      if (this.selectedPoint && this.selectedPoint.feature == point.feature) {
        return;
      }
      if (point.feature.getGeometry().flatCoordinates.length > 2) {
        this.highlightHoverPolygon(point);
      } else {
        this.highlightHoverPoint(point);
      }
    });
    window.EventBus.$on("unhighlightPointOnMap", point =>
      this.unhighlightHoverPoint(point)
    );
    window.EventBus.$on("selectPointAndFitViewOnMap", point =>
      this.selectPointAndFitView(point)
    );
  },
  watch: {
    currentPointLayerHoverPoint: function(newVal, oldVal) {
      if (oldVal) {
        // de-highlight
        this.unhighlightHoverPoint(oldVal);
      }
      if (newVal) {
        if (
          this.selectedPoint &&
          this.selectedPoint.feature == newVal.feature
        ) {
          // dont change highlighting when hovering over selected point
          return;
        }
        // highlight
        newVal.layerType = newVal.layer.layerTypeId;
        newVal.layerType = this.pointLayerTypes.find(
          layerType => layerType.layertypesId == newVal.layer.get("layerTypeId")
        );
        if (newVal.feature.getGeometry().flatCoordinates.length > 2) {
          this.highlightHoverPolygon(newVal);
        } else {
          this.highlightHoverPoint(newVal);
        }
      }
    },
    selectedPoint: function(newVal, oldVal) {
      if (oldVal) {
        // de-highlight
        this.unhighlightSelectedPoint(oldVal);
      }
      if (newVal) {
        // highlight
        if (newVal.feature.getGeometry().flatCoordinates.length > 2) {
          this.highlightSelectedPolygon(newVal);
        } else {
          this.highlightSelectedPoint(newVal);
        }
        // display overlay
        this.selectedPointPopupOverlay.setPosition(
          newVal.feature.getGeometry().flatCoordinates
        );
      }
    }
  },
  methods: {
    // set this.currentPointLayerHoverPoint (point layer point under cursor)
    highlightOnPointerMove(e) {
      var pointLayerFeature = null;
      // handle polygon-shaped point highlighting:
      // stay highlighted when cursor coords are within highlighted polygon
      if (
        this.currentPointLayerHoverPoint &&
        this.currentPointLayerHoverPoint.feature.getGeometry().flatCoordinates
          .length > 2 &&
        containsCoordinate(
          this.currentPointLayerHoverPoint.feature.getGeometry().getExtent(),
          e.coordinate
        )
      ) {
        return;
      }
      // find point feature at cursor position
      this.map.forEachFeatureAtPixel(e.pixel, (feature, layer) => {
        let pointLayerType = this.pointLayerTypes.find(
          layerType => layerType.layertypesId == layer.get("layerTypeId")
        );
        if (pointLayerType) {
          pointLayerFeature = { feature: feature, layer: layer };
        }
      });
      if (!this.currentPointLayerHoverPoint && pointLayerFeature) {
        // no point hover -> point hover
        document.body.style.cursor = "pointer";
        this.currentPointLayerHoverPoint = pointLayerFeature;
      } else if (this.currentPointLayerHoverPoint && !pointLayerFeature) {
        // point hover -> no point hover
        document.body.style.cursor = "auto";
        this.currentPointLayerHoverPoint = null;
      } else if (
        // point hover -> point hover (different point)
        this.currentPointLayerHoverPoint &&
        this.currentPointLayerHoverPoint.feature != pointLayerFeature.feature
      ) {
        this.currentPointLayerHoverPoint = pointLayerFeature;
      }
    },
    // hover point highlighting
    highlightHoverPoint(point) {
      point.feature.setStyle(
        new Style({
          image: new CircleStyle({
            fill: new Fill({
              color: point.layerType.color
            }),
            stroke: new Stroke({
              color: point.layerType.color,
              width: 1
            }),
            radius: 7
          }),
          zIndex: Infinity
        })
      );
    },
    // hover point highlighting for points that are polygons
    highlightHoverPolygon(point) {
      point.feature.setStyle(
        new Style({
          // TODO: why does this fill never work ?
          fill: new Fill({
            color: "black"
          }),
          stroke: new Stroke({
            color: point.layerType.color,
            width: 5
          }),
          zIndex: Infinity
        })
      );
    },
    // remove hover point highlighting
    unhighlightHoverPoint(point) {
      if (!this.selectedPoint || this.selectedPoint.feature != point.feature) {
        // unhighlight only if point != this.selectedPoint
        point.feature.setStyle();
      }
    },
    // selected point highlighting
    highlightSelectedPoint(point) {
      point.feature.setStyle(
        new Style({
          image: new CircleStyle({
            fill: new Fill({
              color: point.layerType.color
            }),
            stroke: new Stroke({
              color: "black",
              width: 2
            }),
            radius: 7
          })
        })
      );
    },
    // selected point highlighting for points that are polygons
    highlightSelectedPolygon(point) {
      point.feature.setStyle(
        new Style({
          // TODO: why does this fill never work ?
          fill: new Fill({
            color: "black"
          }),
          stroke: new Stroke({
            color: "black",
            width: 5
          }),
          zIndex: Infinity
        })
      );
    },
    // remove selected point highlighting
    unhighlightSelectedPoint(point) {
      if (this.selectedPoint && this.selectedPoint.feature == point.feature) {
        return;
      }
      if (this.currentPointLayerHoverPoint == point) {
        this.highlightHoverPoint(point);
      } else {
        point.feature.setStyle();
      }
    },
    // point selection toggle
    togglePointSelect(_, point) {
      point = point ? point : this.currentPointLayerHoverPoint;
      if (point) {
        if (this.selectedPoint && point.feature == this.selectedPoint.feature) {
          // toggle off when clicking on already selected point
          this.selectedPoint = null;
        } else {
          // toggle on
          this.selectedPoint = point;
        }
      } else {
        // toggle off when clicking anywhere else on map
        this.selectedPoint = null;
      }
    },
    // select and center/zoom map view onto point
    selectPointAndFitView(point) {
      this.map.updateSize(); // TODO: this probably only needs to be called once, but where?
      this.togglePointSelect(null, point);
      if (this.selectedPoint) {
        this.map.getView().fit(point.feature.getGeometry(), {
          maxZoom: 15,
          duration: 200
        });
      }
    }
  }
};
